import React, { useEffect, useState } from 'react';

import { ist_day } from '../shared/ist';
import { SelectFilter, Segmented } from '../shared/controls';
import FilePanel from './file_panel';
import HolidaysPanel from './holidays_panel';
import ResponsePanel from './response_panel';
import TeacherMonthDialog from './teacher_month_dialog';
import { ClaimsPanel, DeletedPanel, HeldPanel } from './queues';
import { DELETED_VIEW, deleted_section_label } from './deleted_alert';
import { parse_payouts_filters, payouts_query, PAYOUTS_URL_KEYS } from './url_filters';
import { useInitialQuery, useQueryMirror } from '../shared/use_query_mirror';
import './payouts.css';

/*
 * the Bank payouts tab (/teacher-payments/bank-payouts). The bank file and the bank response use
 * the header's month; held lines, reports, deleted accounts and holidays span every month.
 */
const VIEWS = [
  { key: 'file', label: 'Bank file' },
  { key: 'response', label: 'Bank response' },
  { key: 'held', label: 'Held lines' },
  { key: 'claims', label: 'Payment not received' },
  { key: 'deleted', label: 'Deleted accounts' },
  { key: 'holidays', label: 'Bank holidays' }
];

const BankPayoutsTab = ({
  pay_month,
  month_label,
  months,
  now,
  on_error,
  on_notify,
  view_request,
  deleted_count = 0,
  on_changed
}) => {
  const req_view = view_request && view_request.view;
  const req_rev = view_request && view_request.rev;
  const [view, setView] = useState(() =>
    VIEWS.some((v) => v.key === req_view) ? req_view : 'file'
  );
  useEffect(() => {
    if (VIEWS.some((v) => v.key === req_view)) setView(req_view);
  }, [req_view, req_rev]);
  const [open, setOpen] = useState(null); // {teacher_uuid, pay_month}
  // Bumped after a drill-down write so the panel behind it re-reads.
  const [rev, setRev] = useState(0);
  const today = ist_day(now());
  // Section filters live here, not in the panels, so the URL can carry them and a re-read keeps them.
  const url = useInitialQuery((search) => parse_payouts_filters(search, today));
  const [claim_status, setClaimStatus] = useState(url.status);
  const [holiday_year, setHolidayYear] = useState(url.year);
  useQueryMirror(
    PAYOUTS_URL_KEYS,
    payouts_query({ view, status: claim_status, year: holiday_year }, today)
  );
  const view_options = VIEWS.map((v) => ({
    value: v.key,
    label: v.key === DELETED_VIEW ? deleted_section_label(v.label, deleted_count) : v.label
  }));
  const open_teacher = (teacher_uuid, month) =>
    setOpen({ teacher_uuid, pay_month: month || pay_month });

  return (
    <div className="tp-po-tab">
      <div className="tp-po-bar">
        {/* Phones (<480px): one select replaces the segments; not "Section", the page select sits above. */}
        <SelectFilter
          id="tp-po-section"
          className="tp-po-pick"
          label="Payouts view"
          aria_label="Payouts view"
          value={view}
          options={view_options}
          active={false}
          onChange={setView}
        />
        <Segmented
          className="tp-po-seg"
          label="Bank payouts section"
          options={view_options}
          value={view}
          onChange={setView}
        />
      </div>

      {view === 'file' ? (
        <FilePanel
          key={`${pay_month}-${rev}`}
          pay_month={pay_month}
          month_label={month_label}
          months={months}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'response' ? (
        <ResponsePanel
          key={pay_month}
          pay_month={pay_month}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'held' ? (
        <HeldPanel
          key={rev}
          today={today}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'claims' ? (
        <ClaimsPanel
          key={rev}
          status={claim_status}
          on_status={setClaimStatus}
          on_open_teacher={open_teacher}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}
      {view === 'deleted' ? (
        <DeletedPanel key={rev} on_open_teacher={open_teacher} on_error={on_error} />
      ) : null}
      {view === 'holidays' ? (
        <HolidaysPanel
          today={today}
          year={holiday_year}
          on_year={setHolidayYear}
          on_notify={on_notify}
          on_error={on_error}
        />
      ) : null}

      {open ? (
        <TeacherMonthDialog
          pay_month={open.pay_month}
          teacher_uuid={open.teacher_uuid}
          now={now}
          on_close={() => {
            setOpen(null);
          }}
          on_changed={() => {
            setRev((r) => r + 1);
            if (on_changed) on_changed();
          }}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

export default BankPayoutsTab;
